import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, Download, ExternalLink, ChevronRight } from 'lucide-react'
import { jsPDF } from 'jspdf'
import { getResultadosData } from '../../api/encuestas.api'
import type { ResultadosData } from '../../types'

export function ResultadosPage() {
  const { uuid, sesionUuid } = useParams<{ uuid: string; sesionUuid: string }>()
  const [data, setData] = useState<ResultadosData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!uuid || !sesionUuid) return
    let cancelled = false

    async function load() {
      try {
        const result = await getResultadosData(uuid!, sesionUuid!)
        if (!cancelled) setData(result)
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [uuid, sesionUuid])

  // Favicon y título
  useEffect(() => {
    if (!data) return
    document.title = `Resultados - ${data.encuesta_titulo}`
    if (data.encuesta_imagen) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link) }
      link.href = data.encuesta_imagen
    }
    return () => { document.title = 'Tonal' }
  }, [data])

  const handleDescargarPDF = () => {
    if (!data) return
    const doc = new jsPDF()
    const colorPrimario: [number, number, number] = [37, 99, 235]
    const colorTexto: [number, number, number] = [31, 41, 55]
    const colorSecundario: [number, number, number] = [107, 114, 128]
    let y = 20

    // Header gradient
    const numBands = 35
    const bandHeight = 35 / numBands
    for (let i = 0; i < numBands; i++) {
      const ratio = i / (numBands - 1)
      const r = Math.round(1 + (162 - 1) * ratio)
      const g = Math.round(91 + (37 - 91) * ratio)
      const b = Math.round(154 + (104 - 154) * ratio)
      doc.setFillColor(r, g, b)
      doc.rect(0, i * bandHeight, 210, bandHeight + 0.5, 'F')
    }

    doc.setTextColor(255, 255, 255)
    doc.setFontSize(16)
    doc.setFont(undefined as unknown as string, 'bold')
    doc.text('RESULTADOS DE EVALUACIÓN', 105, 18, { align: 'center' })
    doc.setFontSize(11)
    doc.text(data.encuesta_titulo, 105, 26, { align: 'center' })

    y = 50

    // Info paciente
    if (data.nombre_paciente) {
      doc.setTextColor(...colorPrimario)
      doc.setFontSize(13)
      doc.setFont(undefined as unknown as string, 'bold')
      doc.text('INFORMACIÓN DEL PACIENTE', 20, y)
      y += 8

      doc.setTextColor(...colorTexto)
      doc.setFontSize(10)
      doc.setFont(undefined as unknown as string, 'bold')
      doc.text('Médico: ', 20, y)
      doc.setFont(undefined as unknown as string, 'normal')
      doc.text(data.nombre_paciente, 20 + doc.getTextWidth('Médico: '), y)
      y += 6

      const fecha = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
      doc.setFont(undefined as unknown as string, 'bold')
      doc.text('Fecha de evaluación: ', 20, y)
      doc.setFont(undefined as unknown as string, 'normal')
      doc.text(fecha, 20 + doc.getTextWidth('Fecha de evaluación: '), y)
      y += 12
    }

    // Resultados
    if (data.puntaje_total !== null) {
      doc.setTextColor(...colorPrimario)
      doc.setFontSize(13)
      doc.setFont(undefined as unknown as string, 'bold')
      doc.text('RESULTADOS', 20, y)
      y += 8

      doc.setTextColor(...colorTexto)
      doc.setFontSize(10)
      doc.setFont(undefined as unknown as string, 'bold')
      doc.text('Puntaje total: ', 20, y)
      doc.setFont(undefined as unknown as string, 'normal')
      doc.text(`${data.puntaje_total} puntos`, 20 + doc.getTextWidth('Puntaje total: '), y)
      y += 6

      if (data.resultado) {
        doc.setFont(undefined as unknown as string, 'bold')
        doc.text('Clasificación: ', 20, y)
        doc.setFont(undefined as unknown as string, 'normal')
        doc.text(data.resultado.titulo, 20 + doc.getTextWidth('Clasificación: '), y)
        y += 6

        doc.setFont(undefined as unknown as string, 'bold')
        doc.text('Rango: ', 20, y)
        doc.setFont(undefined as unknown as string, 'normal')
        doc.text(`${data.resultado.min} - ${data.resultado.max} puntos`, 20 + doc.getTextWidth('Rango: '), y)
        y += 10

        if (data.resultado.descripcion) {
          doc.setTextColor(...colorSecundario)
          doc.setFontSize(9)
          const lines = doc.splitTextToSize(data.resultado.descripcion, 170)
          doc.text(lines, 20, y)
          y += lines.length * 5 + 10
        }
      }
    }

    // Respuestas
    doc.setTextColor(...colorPrimario)
    doc.setFontSize(13)
    doc.setFont(undefined as unknown as string, 'bold')
    doc.text('RESPUESTAS', 20, y)
    y += 8

    doc.setFontSize(9)
    data.respuestas.forEach((r, index) => {
      if (y > 250) { doc.addPage(); y = 20 }

      doc.setTextColor(...colorTexto)
      doc.setFont(undefined as unknown as string, 'bold')
      const pLines = doc.splitTextToSize(`${index + 1}. ${r.etiqueta}`, 170)
      doc.text(pLines, 20, y)
      y += pLines.length * 4 + 2

      doc.setTextColor(...colorSecundario)
      doc.setFont(undefined as unknown as string, 'normal')
      const rLines = doc.splitTextToSize(`   ${r.valor_display}`, 170)
      doc.text(rLines, 20, y)
      y += rLines.length * 4 + 5
    })

    // Footer
    if (data.encuesta_footer) {
      if (y > 260) { doc.addPage(); y = 20 }
      y += 10
      doc.setTextColor(150, 150, 150)
      doc.setFontSize(7)
      doc.setFont(undefined as unknown as string, 'italic')
      const footerLines = doc.splitTextToSize(data.encuesta_footer, 170)
      doc.text(footerLines, 105, y, { align: 'center' })
    }

    doc.save(`Resultados_Evaluacion_${data.nombre_paciente || 'Paciente'}.pdf`)
  }

  // totalPasos = bienvenida + campos + resultados (igual que en EncuestaPublicaPage)
  // Usa total_campos del backend, con fallback a campos.length (compatibilidad)
  const totalPasos = data ? (data.total_campos ?? data.campos.length) + 2 : 0

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #015b9a 0%, #b12063 100%)' }}>
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #015b9a 0%, #b12063 100%)' }}>
        <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-8 max-w-md text-center">
          <p className="text-gray-500">No se encontraron resultados.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-3 sm:p-4" style={{ background: 'linear-gradient(135deg, #015b9a 0%, #b12063 100%)' }}>
      <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-4 sm:p-6 lg:p-8 max-w-3xl w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Logo */}
        {data.encuesta_imagen && (
          <div className="flex justify-center mb-4 sm:mb-6">
            <img src={data.encuesta_imagen} alt={data.encuesta_titulo} className="max-w-48 w-auto h-16 sm:max-w-64 sm:h-20 lg:max-w-80 lg:h-24 object-contain" />
          </div>
        )}

        {/* Step indicators - todos completados */}
        <div className="flex items-center justify-center mb-3 sm:mb-4 px-1 sm:px-4 lg:px-6">
          <div className="flex items-center">
            {Array.from({ length: totalPasos }).map((_, i) => (
              <div key={i} className="flex items-center">
                <div className="flex flex-col items-center">
                  <span className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 text-[10px] sm:text-xs font-semibold mb-1 border-blue-600 bg-blue-600 text-white">
                    {i + 1}
                  </span>
                  <span className="text-xs font-medium hidden sm:block text-blue-600">
                    Paso {i + 1}
                  </span>
                </div>
                {i < totalPasos - 1 && (
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mx-0.5 sm:mx-2 lg:mx-4" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Progress bar - 100% */}
        <div className="bg-blue-100 h-2 rounded-full overflow-hidden mb-6 sm:mb-8">
          <div
            className="h-full bg-gradient-to-r from-blue-600 to-blue-700 transition-all duration-300 rounded-full"
            style={{ width: '100%' }}
          />
        </div>

        <div className="space-y-6">
          {/* Gracias */}
          <div className="text-center mb-4 sm:mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">¡Gracias!</h2>
            <p className="text-sm sm:text-base text-gray-600">Aquí están los resultados</p>
          </div>

          {/* Puntaje y resultado */}
          {data.puntaje_total !== null && (
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl p-4 sm:p-8 text-center">
              <div className="text-4xl sm:text-6xl font-bold text-blue-600 mb-1 sm:mb-2">{data.puntaje_total}</div>
              <div className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Puntos totales</div>

              {data.resultado && (
                <div className="bg-white rounded-lg p-4 sm:p-6 mb-3 sm:mb-4">
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">{data.resultado.titulo}</h3>
                  {data.resultado.descripcion && <p className="text-sm sm:text-base text-gray-600">{data.resultado.descripcion}</p>}
                  <div className="mt-3 sm:mt-4 inline-block bg-blue-100 text-blue-800 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">
                    Rango: {data.resultado.min} - {data.resultado.max} puntos
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botón PDF */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleDescargarPDF}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:shadow-lg transition-all inline-flex items-center"
            >
              <Download className="mr-2 h-5 w-5" />
              Descargar PDF
            </button>
          </div>

          {/* Link redirección */}
          {data.url_redireccion && (
            <div className="text-center">
              <a
                href={data.url_redireccion}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Más información
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        {data.encuesta_footer && (
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">{data.encuesta_footer}</p>
          </div>
        )}
      </div>
    </div>
  )
}
